<template>
  <div class="uk-flex-center uk-margin-medium-top" uk-grid>

    <ErrorModal id="ActionErrorModal" :message="error_message" />

    <ConfirmationModal id="ConfirmDeleteVirtualMachine">
      <template v-slot:header>
        <h2>Permanently delete VM?</h2>
      </template>
      <template v-slot:body>
        <p class="uk-text-danger">Deleting your virtual machine is a non-reversible action. After deletion, you will no longer have access to any resources related to this machine, including backups and ips.</p>
      </template>
      <template v-slot:footer>
        <div class="uk-button-group uk-float-right">
          <BitButton color="inverse" @click.native="cancel_delete_virtual_machine">Cancel</BitButton>
          <BitButton color="red" @click.native="confirm_delete_virtual_machine">Delete</BitButton>
        </div>
      </template>
    </ConfirmationModal>

    <ConfirmationModal id="GetBackupName">
      <template v-slot:header>
        <h2>Choose your backup name</h2>
      </template>
      <template v-slot:body>
        <div class="uk-width-expand">
          <label class="uk-form-label uk-text-bold" for="name">Backup Name</label>
          <div class="uk-form-controls">
            <input class="uk-input" type="text" name="name" v-model="new_backup_name" @keydown.space.prevent maxlength="50" pattern="[a-zA-Z0-9]+" required>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <div class="uk-button-group uk-float-right">
          <BitButton color="inverse" @click.native="cancel_create_backup">Cancel</BitButton>
          <BitButton color="green" @click.native="confirm_create_backup" type="submit" :disabled="!backup_name_is_valid">Backup</BitButton>
        </div>
      </template>
    </ConfirmationModal>

    <ConfirmationModal id="ConfirmDeleteSelectedBackups">
      <template v-slot:header>
        <h2>Permanently delete backups?</h2>
      </template>
      <template v-slot:body>
        <p class="uk-text-danger">Deleting the following backups is a non-reversible action.</p>
        <ul class="uk-list uk-list-large uk-list-bullet">
          <li v-for="(backup, index) in selected_backups" :key="index"> {{ backup.backup_name }} </li>
        </ul>
      </template>
      <template v-slot:footer>
        <div class="uk-button-group uk-float-right">
          <BitButton color="inverse" @click.native="cancel_delete_selected_backups">Cancel</BitButton>
          <BitButton color="red" @click.native="confirm_delete_selected_backups">{{ selected_backups.length == 1 ? 'Delete Backup' : 'Delete Backups'}}</BitButton>
        </div>
      </template>
    </ConfirmationModal>

    <ConfirmationModal id="ConfirmRestoreSelectedBackup">
      <template v-slot:header>
        <h2>Restore backup {{ selected_backups[0] ? selected_backups[0].backup_name : '' }}?</h2>
      </template>
      <template v-slot:body>
        <p class="uk-text-danger">You will permanently lose the current state of the machine.</p>
      </template>
      <template v-slot:footer>
        <div class="uk-button-group uk-float-right">
          <BitButton color="inverse" @click.native="cancel_restore_selected_backup">Cancel</BitButton>
          <BitButton color="green" @click.native="confirm_restore_selected_backup">Restore</BitButton>
        </div>
      </template>
    </ConfirmationModal>

    <div class="uk-width-2-3@l uk-width-1-1@m">
      <Card removeHeaderDivider="true">
        <template v-slot:header>
          <progress id="vm-action-progress" class="uk-progress ba-action-progress" value="10" max="100"></progress>

          <div class="uk-flex uk-grid-medium uk-margin-small-bottom" uk-grid>
            <div class="uk-width-auto uk-flex uk-flex-middle">
              <span uk-icon="icon: desktop; ratio: 2.25;"></span>
            </div>
            <div class="uk-width-expand uk-flex uk-flex-middle">
              <div class="uk-display-block uk-text-left">
                <h2 class="uk-margin-remove-bottom"> {{ virtual_machine.hostname }}</h2>
                <h5 class="uk-h5 uk-margin-remove"> {{ virtual_machine.ips[0].ip }} </h5>
              </div>
            </div>
          </div>
          <div class="uk-width-expand ba-remove-parent-header-padding">
            <div class="ba-card-header-tabs">
              <ul class="uk-flex-left" uk-tab>
                <router-link :to="{ name: 'ViewVirtualMachine', params: { id: virtual_machine._id } }" v-slot="{ href, route, navigate, isActive, isExactActive }">
                  <li :class="[isExactActive && 'uk-active', currentRoute.includes('backups') ? '' : 'uk-active']">
                    <a :href="href" @click="navigate">
                      instance
                    </a>
                  </li>
                </router-link>
                <router-link :to="{ name: 'ViewVirtualMachineBackups', params: { id: virtual_machine._id } }" v-slot="{ href, route, navigate, isActive, isExactActive }">
                  <li :class="[isExactActive && 'uk-active']">
                    <a :href="href" @click="navigate">
                      backups
                    </a>
                  </li>
                </router-link>
              </ul>
            </div>
          </div>

        </template>
        <template v-slot:body>
          <!-- Instance -->
          <div class="uk-width-expand ba-remove-parent-body-padding" v-if="!currentRoute.includes('backups')">
            <div class="uk-button-group uk-width-expand uk-overflow-auto">
              <BitButton color="transparent" @click.native="copy_vm_password_to_clipboard">
                <span uk-icon="icon: lock; ratio: 1.3;"></span>
                copy password
              </BitButton>
              <BitButton color="transparent" :disabled="disableStart" @click.native="change_vm_state('start')">
                <span uk-icon="icon: play; ratio: 1.75;"></span>
                start
              </BitButton>
              <BitButton color="transparent" :disabled="disableStop" @click.native="change_vm_state('stop')">
                <span uk-icon="icon: ban; ratio: 1.2;"></span>
                stop
              </BitButton>
              <BitButton color="transparent" :disabled="disableRestart" @click.native="change_vm_state('reboot')">
                <span uk-icon="icon: refresh; ratio: 1.25;"></span>
                restart
              </BitButton>
              <BitButton color="transparent" :disabled="isDeploying" @click.native="submit_delete_virtual_machine">
                <span uk-icon="icon: trash; ratio: 1.2;"></span>
                delete
              </BitButton>
            </div>
          </div>
          <div class="ba-height-min-large uk-flex uk-flex-middle" uk-grid v-if="!currentRoute.includes('backups')">
            <div class="uk-width-1-1">
              <div class="uk-placeholder uk-text-center">virtual machine graphing coming soon</div>
            </div>
          </div>

          <!-- Backups -->
          <div class="uk-width-expand ba-remove-parent-body-padding" v-if="currentRoute.includes('backups')">
            <div class="uk-button-group uk-width-expand uk-overflow-auto">
              <BitButton color="transparent" :disabled="disableRestore" @click.native="get_restore_selected_backup_confirmation">
                <span uk-icon="icon: history; ratio: 1.25;"></span>
                restore
              </BitButton>
              <BitButton color="transparent" :disabled="isDeploying" @click.native="create_backup">
                <span uk-icon="icon: database; ratio: 1.25;"></span>
                backup
              </BitButton>
              <BitButton color="transparent" :disabled="disableDeleteBackups" @click.native="get_delete_selected_backups_confirmation">
                <span uk-icon="icon: trash; ratio: 1.2;"></span>
                delete
              </BitButton>
            </div>
          </div>
          <div class="ba-height-min-large" uk-grid v-if="currentRoute.includes('backups')">
            <!-- backups table -->
            <div class="uk-width-1-1 uk-margin-top" v-if="backups.length > 0">
              <div class="uk-overflow-auto">
                <table class="uk-table uk-table-divider uk-table-hover uk-table-small">
                  <thead>
                    <tr>
                      <th class="uk-table-shrink"></th>
                      <th class="uk-table-expand uk-text-nowrap">Name</th>
                      <th class="uk-width-small">Date</th>
                      <th class="uk-width-small">Time (UTC)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(backup, index) in backups" :key="index">
                      <td><input type="checkbox" class="uk-checkbox" :id="index" :value="backup" v-model="selected_backups"></td>
                      <td>{{ backup.backup_name }}</td>
                      <td>{{ backup.backup_date }}</td>
                      <td>{{ backup.backup_time }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- placeholder -->
            <div class="uk-width-1-1 uk-margin-top uk-flex uk-flex-middle uk-flex-center" v-else>
              <div class="uk-text-center">
                <span uk-icon="icon: info; ratio: 3.25"></span>
                <h4 class="uk-h4 uk-text-muted">You don't have any backups yet.</h4>
              </div>
            </div>

          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import BitButton from '@/components/common/button.vue'
import Card from '@/components/common/card.vue';
import ConfirmationModal from '@/components/portal/confirmationmodal.vue';
import ErrorModal from '@/components/portal/errormodal.vue';

import { mapActions, mapGetters } from 'vuex';

import UIkit from 'uikit';

export default {
  name: 'ViewVirtualMachine',
  components: {
    BitButton,
    Card,
    ConfirmationModal,
    ErrorModal
  },
  data() {
    return {
      selected_backups: [],
      new_backup_name: null,
      error_message: null
    }
  },
  computed: {
    ...mapGetters('vm', ['getVMById', 'getVMBackupsById']),
    virtual_machine() {
      return this.getVMById(this.$route.params.id)
    },
    backups() {
      return this.getVMBackupsById(this.$route.params.id)
    },
    isDeploying() {
      return this.virtual_machine.state == 'initialized' ? true :
             this.virtual_machine.state == 'deploying' ? true : false
    },
    isRunning() {
      return this.virtual_machine.state == 'running' ? true : false
    },
    isStopped() {
      return this.virtual_machine.state == 'stopped' ? true : false
    },
    disableStart() {
      if( this.isDeploying || this.isRunning )
        return true
      return false
    },
    disableStop() {
      if( this.isDeploying || this.isStopped )
        return true
      return false
    },
    disableRestart() {
      if( this.isDeploying || this.isStopped )
        return true
      return false
    },
    disableDeleteBackups() {
      return this.selected_backups.length == 0;
    },
    disableRestore() {
      return this.selected_backups.length != 1;
    },
    backup_name_is_valid() {
      const regex = /^[a-zA-Z0-9]+$/
      if( this.new_backup_name == null)
        return false
      else if( regex.test(this.new_backup_name) )
        return true
      return false
    },
    currentRoute() {
      return this.$route.path;
      // This currentRoute.includes fix is needed because isActive and isExactActive for some reason do not calculate properly for ViewVirtualMachine route
      // on first mount of the component. So, if you visited this page for the first time, 'instance' would not be highlighted as active
    }
  },
  created() {
    this.selected_backups = [];
    this.new_backup_name = null;
    this.error_message = null;
  },
  methods: {
    ...mapActions('vm', ['GET_VIRTUAL_MACHINE_PASSWORD', 'CHANGE_VIRTUAL_MACHINE_STATE', 'DELETE_VIRTUAL_MACHINE', 'BACKUP_VIRTUAL_MACHINE', 'DELETE_VIRTUAL_MACHINE_BACKUP', 'RESTORE_VIRTUAL_MACHINE_BACKUP']),
    initialize_progress_bar() {
      var bar = document.getElementById('vm-action-progress');
      bar.value = 0;
    },
    run_progress_bar(multiplier) {
      UIkit.util.ready( () => {
        var bar = document.getElementById('vm-action-progress');

        var animate = setInterval(function () {
          bar.value += 2;
          if (bar.value >= bar.max) {
            clearInterval(animate);
            bar.value = 0;
          }
        }, multiplier * 10);
      });
    },
    copy_vm_password_to_clipboard() {
      this.run_progress_bar(.1);
      this.GET_VIRTUAL_MACHINE_PASSWORD(this.virtual_machine._id)
        .then( (password) => {
          // Create new element
          var el = document.createElement('textarea');
          // Set value (string to be copied)
          el.value = password;
          // Set non-editable to avoid focus and move outside of view
          el.setAttribute('readonly', '');
          el.style = {position: 'absolute', left: '-9999px'};
          document.body.appendChild(el);
          // Select text inside element
          el.select();
          // Copy text to clipboard
          document.execCommand('copy');
          // Remove temporary element
          document.body.removeChild(el);
        }).catch( () => {
          this.error_message = "There was an error retrieving your VM password. Please try again or contact support."
          UIkit.modal('#ActionErrorModal').show();
        })
    },
    change_vm_state(new_state) {
      this.error_message = null;
      this.run_progress_bar(1.5);
      let vm = {
        id: this.virtual_machine._id,
        state: new_state
      }
      this.CHANGE_VIRTUAL_MACHINE_STATE(vm)
        .catch( () => {
          this.error_message = "There was an error changing the state of your VM. Please try again or contact support."
          UIkit.modal('#ActionErrorModal').show();
        })
    },
    cancel_delete_virtual_machine() {
      UIkit.modal('#ConfirmDeleteVirtualMachine').hide();
    },
    submit_delete_virtual_machine() {
      UIkit.modal('#ConfirmDeleteVirtualMachine').show();
    },
    confirm_delete_virtual_machine() {
      UIkit.modal('#ConfirmDeleteVirtualMachine').hide();
      this.error_message = null;
      this.run_progress_bar(3);

      this.DELETE_VIRTUAL_MACHINE(this.virtual_machine._id)
        .then( () => {
          this.$router.push( { name: 'VirtualMachines' } );
        }).catch( () => {
          this.error_message = "There was an error deleting your VM. Please try again or contact support."
          UIkit.modal('#ActionErrorModal').show();
        })
    },
    create_backup() {
      UIkit.modal('#GetBackupName').show();
    },
    cancel_create_backup() {
      UIkit.modal('#GetBackupName').hide();
    },
    confirm_create_backup() {
      this.error_message = null;
      UIkit.modal('#GetBackupName').hide();
      this.run_progress_bar(3);

      let backup = {
        id: this.virtual_machine._id,
        name: this.new_backup_name
      }

      this.BACKUP_VIRTUAL_MACHINE(backup)
        .then( () => {
          this.new_backup_name = null;
        }).catch( () => {
          this.error_message = "There was an error backing up your VM. Please try again or contact support."
          UIkit.modal('#ActionErrorModal').show();
        })
    },
    get_delete_selected_backups_confirmation() {
      UIkit.modal('#ConfirmDeleteSelectedBackups').show();
    },
    cancel_delete_selected_backups() {
      UIkit.modal('#ConfirmDeleteSelectedBackups').hide();
    },
    confirm_delete_selected_backups() {
      this.error_message = null;
      UIkit.modal('#ConfirmDeleteSelectedBackups').hide();
      this.run_progress_bar(this.selected_backups.length * 1);

      this.selected_backups.forEach( (backup) => {
        this.DELETE_VIRTUAL_MACHINE_BACKUP( { id: this.virtual_machine._id, backup_id: backup._id } )
          .catch( () => {
            this.error_message = "There was an error deleting your backup. Please try again or contact support."
            UIkit.modal('#ActionErrorModal').show();
          })
      })
    },
    get_restore_selected_backup_confirmation() {
      UIkit.modal('#ConfirmRestoreSelectedBackup').show();
    },
    cancel_restore_selected_backup() {
      UIkit.modal('#ConfirmRestoreSelectedBackup').hide();
    },
    confirm_restore_selected_backup() {
      this.error_message = null;
      UIkit.modal('#ConfirmRestoreSelectedBackup').hide();
      this.run_progress_bar(10);

      this.RESTORE_VIRTUAL_MACHINE_BACKUP( { id: this.virtual_machine._id, backup_id: this.selected_backups[0]._id } )
        .then( () => {
          this.selected_backups = []
        })
        .catch( () => {
          this.error_message = "There was an error deleting your backup. Please try again or contact support."
          UIkit.modal('#ActionErrorModal').show();
        })
    }
  },
  mounted() {
    this.initialize_progress_bar();
  }
}
</script>

<style scoped lang="less">
@import "../../../assets/less/theme.less";

.uk-tab {
  margin: 0;
  border-bottom: 1px solid @ba-off-white;
  > * {
    > a {
      display: block;
      text-align: center;
      padding: 9px 20px;
      border-bottom: 2px solid transparent;
      font-size: 12px;
      text-transform: uppercase;
      transition: color .1s ease-in-out;
      line-height: 20px;
    }
  }
  > .uk-active {
    > a {
      color: #222;
      border-color: @ba-purple;
    }
  }
}
.uk-button-group {
  padding-top: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid @ba-off-white;
}
.ba-card-body-quick-actions {
  width: ~'calc(100% + 80px)';
}
.ba-card-header-tabs {
  width: ~'calc(100% + 80px)';
}
.ba-remove-parent-header-padding {
  margin-left: -40px;
  margin-right: -40px;
  margin-bottom: -20px;
}
.ba-remove-parent-body-padding {
  margin-left: -40px;
  margin-right: -40px;
  margin-top: -20px;
  max-width: ~'calc(100% + 80px)';
  width: ~'calc(100% + 80px)';
}
.row-selected {
  background-color: #ffd;
}
.ba-action-progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
}
.ba-action-progress::-webkit-progress-value {
  background-color: @ba-gold;
}
.ba-action-progress::-moz-progress-bar {
  background-color: @ba-gold;
}
.ba-action-progress::-ms-fill {
  background-color: @ba-gold;
}
</style>
